<template>
    <el-card class="main-card" v-loading="loading">
        <div slot="header" style="display: flex; align-items: center; gap: 12px">
            <el-button type="primary" size="small" icon="el-icon-arrow-left" @click="back">返回</el-button>
            <span style="font-size: 18px; font-weight: bold">
                {{ detail.templateName || '模板产品型号详情' }}
            </span>
        </div>

        <template v-if="detail.id">
            <!-- 基本信息 -->
            <div class="section-title">基本信息</div>
            <el-descriptions :column="3" border size="medium">
                <el-descriptions-item label="模板ID">{{ detail.id }}</el-descriptions-item>
                <el-descriptions-item label="模板名称">{{ formatValue(detail.templateName) }}</el-descriptions-item>
                <el-descriptions-item label="状态">
                    <el-tag :type="detail.status === 1 ? 'success' : 'info'" size="mini">
                        {{ detail.status === 1 ? '已激活' : '停用' }}
                    </el-tag>
                </el-descriptions-item>
                <el-descriptions-item label="排序">{{ formatValue(detail.sortOrder) }}</el-descriptions-item>
                <el-descriptions-item label="备注">{{ formatValue(detail.remark) }}</el-descriptions-item>
                <el-descriptions-item label="关联型号ID">{{ formatValue(detail.modelId) }}</el-descriptions-item>
                <el-descriptions-item label="创建人">{{ formatValue(detail.createdName) }}</el-descriptions-item>
                <el-descriptions-item label="创建时间">{{ formatValue(detail.createdTime) }}</el-descriptions-item>
                <el-descriptions-item label="更新时间">{{ formatValue(detail.updatedTime) }}</el-descriptions-item>
            </el-descriptions>

            <!-- 关联产品型号 -->
            <template v-if="detail.productModel">
                <div class="section-title">关联产品型号</div>
                <div style="display: flex; gap: 24px; align-items: flex-start">
                    <el-image v-if="detail.productModel.urlHttp" :src="formatImage(detail.productModel.urlHttp)"
                        fit="contain" class="model-image"
                        :preview-src-list="[formatImage(detail.productModel.urlHttp)]" />
                    <el-descriptions :column="2" border size="medium" style="flex: 1">
                        <el-descriptions-item label="型号编码">{{ formatValue(detail.productModel.code) }}</el-descriptions-item>
                        <el-descriptions-item label="型号名称">{{ formatValue(detail.productModel.name) }}</el-descriptions-item>
                        <el-descriptions-item label="型号状态">{{ formatValue(detail.productModel.statusValue) }}</el-descriptions-item>
                        <el-descriptions-item label="支持配置">
                            {{ detail.productModel.isSupportConfig === 1 ? '支持' : '不支持' }}
                        </el-descriptions-item>
                        <el-descriptions-item label="型号描述" :span="2">
                            {{ formatValue(detail.productModel.description) }}
                        </el-descriptions-item>
                    </el-descriptions>
                </div>
            </template>

            <!-- 运维费配置 -->
            <template v-if="detail.productModelOperationFee">
                <div class="section-title">运维费配置</div>
                <el-descriptions :column="3" border size="medium">
                    <el-descriptions-item label="运维费用">
                        {{ formatPrice(detail.productModelOperationFee.feePrice) }}
                    </el-descriptions-item>
                    <el-descriptions-item label="状态">
                        <el-tag :type="detail.productModelOperationFee.status === 1 ? 'success' : 'info'" size="mini">
                            {{ detail.productModelOperationFee.status === 1 ? '已激活' : '停用' }}
                        </el-tag>
                    </el-descriptions-item>
                    <el-descriptions-item label="更新时间">
                        {{ formatValue(detail.productModelOperationFee.updatedTime) }}
                    </el-descriptions-item>
                </el-descriptions>
            </template>

            <!-- 产品尺寸 -->
            <template v-if="detail.templateProductSizeList && detail.templateProductSizeList.length">
                <div class="section-title">产品尺寸（{{ detail.templateProductSizeList.length }}）</div>
                <el-table :data="detail.templateProductSizeList" border stripe size="small">
                    <el-table-column type="index" label="序号" width="60" align="center" />
                    <el-table-column label="尺寸名称" min-width="100">
                        <template slot-scope="scope">
                            {{ (scope.row.productSize && scope.row.productSize.name) || '--' }}
                        </template>
                    </el-table-column>
                    <el-table-column label="尺寸编码" min-width="150">
                        <template slot-scope="scope">
                            {{ (scope.row.productSize && scope.row.productSize.code) || '--' }}
                        </template>
                    </el-table-column>
                    <el-table-column label="规格（长×宽×高 m）" min-width="150" align="center">
                        <template slot-scope="scope">
                            <span v-if="scope.row.productSize">
                                {{ scope.row.productSize.length }} × {{ scope.row.productSize.width }} × {{ scope.row.productSize.height }}
                            </span>
                            <span v-else>--</span>
                        </template>
                    </el-table-column>
                    <el-table-column label="内部基础价" min-width="110" align="right">
                        <template slot-scope="scope">
                            {{ scope.row.productSize ? formatPrice(scope.row.productSize.basePriceInner) : '--' }}
                        </template>
                    </el-table-column>
                    <el-table-column label="外部基础价" min-width="110" align="right">
                        <template slot-scope="scope">
                            {{ scope.row.productSize ? formatPrice(scope.row.productSize.basePriceOut) : '--' }}
                        </template>
                    </el-table-column>
                    <el-table-column prop="sortOrder" label="排序" width="70" align="center" />
                    <el-table-column label="状态" width="90" align="center">
                        <template slot-scope="scope">
                            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="mini">
                                {{ scope.row.status === 1 ? '已激活' : '停用' }}
                            </el-tag>
                        </template>
                    </el-table-column>
                </el-table>
            </template>

            <!-- 配置分组 -->
            <template v-if="detail.templateProductGroupList && detail.templateProductGroupList.length">
                <div class="section-title">配置分组（{{ detail.templateProductGroupList.length }}）</div>
                <div v-for="(group, gIndex) in detail.templateProductGroupList" :key="group.groupId || gIndex"
                    class="group-block">
                    <!-- 分组头部 -->
                    <div class="group-header">
                        <el-image v-if="group.configGroup && group.configGroup.urlHttp"
                            :src="formatImage(group.configGroup.urlHttp)" fit="cover" class="group-image"
                            :preview-src-list="[formatImage(group.configGroup.urlHttp)]" />
                        <div style="flex: 1">
                            <div class="group-name">
                                {{ (group.configGroup && group.configGroup.name) || `分组 ${gIndex + 1}` }}
                            </div>
                            <div class="group-code" v-if="group.configGroup && group.configGroup.code">
                                {{ group.configGroup.code }}
                            </div>
                        </div>
                        <span class="group-count">
                            配置项 {{ (group.templateProductItemList || []).length }} 个
                        </span>
                    </div>

                    <!-- 分组下配置项 -->
                    <el-table v-if="group.templateProductItemList && group.templateProductItemList.length"
                        :data="group.templateProductItemList" border stripe size="small">
                        <el-table-column type="index" label="序号" width="60" align="center" />
                        <el-table-column label="配置项" min-width="140">
                            <template slot-scope="scope">
                                {{ itemDisplayName(scope.row) }}
                            </template>
                        </el-table-column>
                        <el-table-column v-for="col in tableColumns(group.templateProductItemList)" :key="col"
                            :prop="col" :label="col" min-width="110" show-overflow-tooltip>
                            <template slot-scope="scope">
                                {{ formatValue(scope.row[col]) }}
                            </template>
                        </el-table-column>
                    </el-table>
                    <div v-else class="group-empty">暂无配置项</div>
                </div>
            </template>
        </template>

        <el-empty v-else-if="!loading" description="未查询到模板详情"></el-empty>
    </el-card>
</template>

<script>
import { templateProductModelApi } from '@/api/quote'
import { formatImage } from '@/utils/format'

export default {
    name: 'TemplateProductModelDetail',
    props: {
        id: {
            type: [String, Number],
            required: true
        }
    },
    data() {
        return {
            loading: false,
            detail: {},
        }
    },
    mounted() {
        this.fetchDetail();
    },
    methods: {
        formatImage,
        // 获取模板详情
        async fetchDetail() {
            this.loading = true;
            try {
                const res = await templateProductModelApi.detail(this.id)
                if (res && res.code === '0000') {
                    this.detail = res.result || {}
                }
            } catch (error) {
                console.error('获取模板产品型号详情失败：', error)
            } finally {
                this.loading = false;
            }
        },

        back() {
            this.$router.back();
        },

        // 空值展示兜底
        formatValue(val) {
            return val !== null && val !== undefined && val !== '' ? val : '--'
        },

        // 金额展示：千分位
        formatPrice(val) {
            if (val === null || val === undefined || val === '') return '--'
            return Number(val).toLocaleString()
        },

        // 提取对象中的基础类型字段（过滤嵌套对象/数组，用于通用表格列）
        primitiveKeys(obj) {
            if (!obj) return [];
            return Object.keys(obj).filter(k => obj[k] === null || typeof obj[k] !== 'object')
        },

        // 根据列表第一条数据生成通用表格列
        tableColumns(list) {
            if (!list || !list.length) return [];
            return this.primitiveKeys(list[0])
        },

        // 配置项展示名：优先自身 name/itemName，其次取第一个带 name/itemName 的嵌套对象（如 configItem.itemName）
        itemDisplayName(item) {
            if (!item) return '--'
            if (item.name) return item.name
            if (item.itemName) return item.itemName
            for (const key of Object.keys(item)) {
                const val = item[key]
                if (val && typeof val === 'object' && !Array.isArray(val)) {
                    if (val.name) return val.name
                    if (val.itemName) return val.itemName
                }
            }
            return '--'
        },
    }
}
</script>

<style scoped>
.main-card {
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    border: none;
    min-height: 600px;
}

.section-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin: 24px 0 12px;
    padding-left: 10px;
    border-left: 3px solid #1890ff;
}

.section-title:first-of-type {
    margin-top: 0;
}

.model-image {
    width: 160px;
    height: 120px;
    border-radius: 8px;
    background-color: #f5f7fa;
    flex-shrink: 0;
}

/* 配置分组 */
.group-block {
    margin-bottom: 20px;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;
}

.group-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background-color: #f8fafc;
    border-bottom: 1px solid #ebeef5;
}

.group-image {
    width: 48px;
    height: 48px;
    border-radius: 6px;
    background-color: #fff;
    flex-shrink: 0;
}

.group-name {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
}

.group-code {
    font-size: 12px;
    color: #9ca3af;
    margin-top: 2px;
}

.group-count {
    font-size: 12px;
    color: #6b7280;
    background-color: #eef2f7;
    padding: 4px 10px;
    border-radius: 10px;
}

.group-empty {
    padding: 20px;
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
}
</style>
